import { z } from 'zod';

export const photoMimeSchema = z.enum([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
]);
export type PhotoMime = z.infer<typeof photoMimeSchema>;

export const MAX_PHOTO_BYTES = 25 * 1024 * 1024; // 25 MiB

export const photoSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  stageId: z.string().nullable(),
  dailyLogId: z.string().nullable(),
  timelineEventId: z.string().nullable(),
  filename: z.string(),
  mimeType: photoMimeSchema,
  sizeBytes: z.number().int().nonnegative(),
  width: z.number().int().nonnegative().nullable(),
  height: z.number().int().nonnegative().nullable(),
  caption: z.string().nullable(),
  url: z.string().url(),
  uploadedBy: z.string(),
  uploadedByName: z.string(),
  uploadedAt: z.string(),
  // Wave 43: flag indicating whether this photo is curated into the
  // public Vitrine (house-sales showcase). Backfilled to `false` for
  // every existing row in migration 0025. Optional in the schema so
  // any legacy consumer that doesn't care about Vitrine can ignore it.
  publishToShowcase: z.boolean().optional(),
  // ObraFit · migration 0046 — photo can belong to a custom album. Null
  // when the photo isn't placed into an album. Optional so legacy
  // callers (and the timeline drawer) keep working unchanged.
  albumId: z.string().nullable().optional(),
});
export type Photo = z.infer<typeof photoSchema>;

export const requestUploadRequestSchema = z.object({
  filename: z.string().trim().min(1).max(255),
  mimeType: photoMimeSchema,
  sizeBytes: z.number().int().positive().max(MAX_PHOTO_BYTES),
  stageId: z.string().optional(),
  dailyLogId: z.string().optional(),
  albumId: z.string().optional(),
  caption: z.string().trim().max(500).optional(),
});
export type RequestUploadRequest = z.infer<typeof requestUploadRequestSchema>;

export const requestUploadResponseSchema = z.object({
  photoId: z.string(),
  uploadUrl: z.string().url(),
  method: z.literal('PUT'),
  headers: z.record(z.string()),
  expiresAt: z.string(),
});
export type RequestUploadResponse = z.infer<typeof requestUploadResponseSchema>;

export const completeUploadRequestSchema = z.object({
  photoId: z.string(),
  width: z.number().int().nonnegative().optional(),
  height: z.number().int().nonnegative().optional(),
});
export type CompleteUploadRequest = z.infer<typeof completeUploadRequestSchema>;
